
import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo } from "react";
import { 
  useRolePermissions, 
  Resource, 
  PermissionType 
} from "@/hooks/rollenbeheer/useRolePermissions";
import { useAuth } from "@/context/AuthContext";

interface PermissionsContextType {
  hasPermission: (resource: Resource, permissionType?: PermissionType) => boolean;
  isLoading: boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { permissions, hasPermission: roleHasPermission, isLoading: permissionsLoading } = useRolePermissions();
  const { isAuthenticated, user } = useAuth();
  const [initialized, setInitialized] = useState(false);

  // Enhanced debug logs for troubleshooting permission issues
  useEffect(() => {
    console.log("PermissionsProvider initialized");
    console.log("isAuthenticated:", isAuthenticated);
    console.log("User role:", user?.role);
    console.log("User data:", user ? { id: user.id, name: user.name } : "No user");
    console.log("Permissions loading:", permissionsLoading);
    if (!permissionsLoading) {
      console.log("Loaded permissions count:", permissions?.length || 0);
      if (user?.role === "management") {
        console.log("MANAGEMENT USER DETECTED - should have all permissions");
      }
    }
  }, [isAuthenticated, user, permissionsLoading, permissions]);

  // Ensure permissions are fully loaded before rendering protected content
  useEffect(() => {
    if (!permissionsLoading && !initialized) {
      console.log("Permissions fully loaded, initializing context");
      setInitialized(true);
    }
  }, [permissionsLoading, initialized]);

  // Improved hasPermission function with better management role handling
  const hasPermission = useCallback((resource: Resource, permissionType: PermissionType = "read"): boolean => {
    // If not authenticated, no permissions
    if (!isAuthenticated) {
      console.log(`Not authenticated, denying access to ${resource}`);
      return false;
    }
    
    if (!user) {
      console.log(`No user found, denying access to ${resource}`);
      return false;
    }
    
    // Management role ALWAYS has access to everything - this is the critical priority check
    if (user.role === "management") {
      console.log(`Management role always has access to ${resource}.${permissionType}`);
      return true;
    }
    
    // Also check functie field for enhanced management access
    if (user.role === "planner" && resource === "medewerkers") {
      console.log(`Planner role has access to ${resource}.${permissionType}`);
      return permissionType !== "delete"; // Planners can read/write but not delete
    }
    
    // Special case for feedback - all roles have access to read/write
    if (resource === "feedback" && (permissionType === "read" || permissionType === "write")) {
      console.log(`All roles have ${permissionType} access to feedback`);
      return true;
    }
    
    // If still loading permissions, deny access
    if (permissionsLoading && !initialized) {
      console.log(`Permissions still loading, denying access to ${resource}`);
      return false;
    }
    
    // Use the role-based permission checker from useRolePermissions
    const result = roleHasPermission(resource, permissionType);
    console.log(`Permission check for ${user.role} - ${resource}.${permissionType}: ${result}`);
    return result;
  }, [roleHasPermission, permissionsLoading, initialized, isAuthenticated, user]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    hasPermission,
    isLoading: permissionsLoading && !initialized
  }), [hasPermission, permissionsLoading, initialized]);

  // Use a consistent loading pattern
  if (!initialized && permissionsLoading) {
    console.log("Rendering loading fallback for permissions");
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-muted-foreground">Rechten laden...</p>
      </div>
    );
  }

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};
